import type {Bandwidth} from '@pexip/peer-connection';
import {createPCSignals} from '@pexip/peer-connection';
import {getErrorCode} from '@pexip/infinity-api';
import {ClientCallType} from './clientCallType';
import type {
    AllowMediaChangeOfDirection,
    APIType,
    CallMode,
    CallOptions,
    CallSignals,
    ClientProps,
    DegradationPreference,
    EventSignals,
    ExtendedInfinityErrorMessage,
    GenerateEndpoint,
    GetEndpointParams,
    InfinitySignals,
    MediaPriority,
    ParticipantID,
    RequestClient,
    Stats,
    Stun,
    Turn,
} from './types';
import {DEFAULT_CLIENT_CALLTYPE, SDP_OFFER_IGNORED} from './constants';
import {
    createMainStatsSignals,
    getBandwidth,
    matchAllowMediaChangeOfDirection,
    matchCallType,
    toClientCallType,
} from './utils';
import {logger} from './logger';
import {createMCUCall} from './callMCU';
import {createSFUCall} from './callSFU';
import {fetchWithDebugLog, fetchWithInfoLog} from './fetcher';
import type {createEventSourceManager} from './eventSource';

const getDefaultProps = () => ({
    infinityCallType: 'video' as 'video' | 'video-only' | 'audio',
    clientCallType: DEFAULT_CLIENT_CALLTYPE,
    call: undefined as
        | ReturnType<typeof createMCUCall>
        | ReturnType<typeof createSFUCall>
        | undefined,
    callUuid: undefined as string | undefined,
    /**
     * Map to the `call_type` from the respond of `request_token`
     * @see {@link https://docs.pexip.com/api_client/api_rest.htm#request_token}
     */
    allowChangeOfMediaDirection: {
        video: true,
        audio: true,
        presentation: true,
    } as AllowMediaChangeOfDirection,

    stun: undefined as Stun | undefined,
    turn: undefined as Turn | undefined,
    dataChannelId: undefined as number | undefined,
    useRelayCandidatesOnly: undefined as boolean | undefined,
    bandwidthOut: undefined as number | undefined,
    bandwidthIn: undefined as number | undefined,
    allow1080p: undefined as boolean | undefined,
    clientAllowH264: undefined as boolean | undefined,
    clientAllowVP8: undefined as boolean | undefined,
    clientAllowVP9: undefined as boolean | undefined,
    allowVP9: undefined as boolean | undefined,
    feccSupported: false,
    latestStats: undefined as Stats | undefined,
    secureCheckCode: '',
    bandwidth: undefined as Bandwidth | undefined,
    callMode: 'WEBRTC' as CallMode,
    numberOfReceiveAudioStreams: undefined as number | undefined,
    numberOfReceiveVideoStreams: undefined as number | undefined,
});

export const createCallManager = ({
    clientProps,
    API,
    signals,
    eventSignals,
    callSignals,
    generateEndpoint,
    getRequestClient,
    getEventSourceManager,
    getBreakoutParticipantUuid,
}: {
    clientProps: ClientProps;
    API: APIType;
    signals: InfinitySignals;
    eventSignals: EventSignals;
    callSignals: CallSignals;
    generateEndpoint: GenerateEndpoint;
    getRequestClient: () => RequestClient | undefined;
    getEventSourceManager: () =>
        ReturnType<typeof createEventSourceManager> | undefined;
    getBreakoutParticipantUuid: (
        breakoutUuid?: string,
    ) => ParticipantID | undefined;
}) => {
    const props = getDefaultProps();

    type Defaults = Pick<
        ClientProps,
        'conferenceAlias' | 'host' | 'participantUuid' | 'breakoutUuid'
    >;
    const withDefaults = <T>(p: T): Defaults & T =>
        ({
            conferenceAlias: clientProps.conferenceAlias,
            host: clientProps.host,
            participantUuid: clientProps.participantUuid,
            breakoutUuid: clientProps.breakoutUuid,
            ...p,
        }) as Defaults & T;

    const timerIDs = {
        update: -1,
        pushStatistics: -1,
    };

    const ack = generateEndpoint({
        func: async ([params]: GetEndpointParams<'ack'>, fetcher) => {
            const {
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                sdp,
                offerIgnored,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            const body = {
                sdp: offerIgnored ? SDP_OFFER_IGNORED : sdp,
                offer_ignored: offerIgnored,
            };

            return API.ack({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                    callUuid,
                },
                body,
                host,
            });
        },
        funcName: 'ack',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const cleanup = () => {
        clearTimeout(timerIDs.update);
        clearTimeout(timerIDs.pushStatistics);
        timerIDs.update = -1;
        timerIDs.pushStatistics = -1;
        Object.assign(props, getDefaultProps());
    };

    /**
     * Tears down the current WebRTC call and notifies the server
     * of the disconnection.
     */
    const disconnect = async (reason?: string) => {
        const callUuid = props.callUuid;
        await teardownCall();
        await disconnectCall({
            callUuid: callUuid,
            conferenceAlias: clientProps.conferenceAlias,
            breakoutUuid: clientProps.breakoutUuid,
            breakoutParticipantUuid: getBreakoutParticipantUuid(
                clientProps.breakoutUuid,
            ),
            host: clientProps.host,
            participantUuid: clientProps.participantUuid,
            reason,
        });
    };

    const disconnectCall = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'disconnectCall'>,
            fetcher,
        ) => {
            const {
                breakoutUuid,
                breakoutParticipantUuid,
                reason,
                conferenceAlias,
                participantUuid,
                callUuid = props.callUuid,
                host,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            if (breakoutUuid && breakoutParticipantUuid) {
                await API.disconnectCall({
                    fetcher,
                    params: {
                        conferenceAlias: conferenceAlias,
                        breakoutUuid: breakoutUuid,
                        callUuid: callUuid,
                        participantUuid: breakoutParticipantUuid,
                    },
                    host,
                });
                return;
            }

            if (reason !== 'Browser closed') {
                try {
                    await API.disconnectCall({
                        fetcher,
                        params: {
                            conferenceAlias: conferenceAlias,
                            participantUuid: participantUuid,
                            callUuid: callUuid,
                        },
                        host,
                    });
                } catch (e) {
                    logger.warn({reason: e}, 'Unable to disconnect a call');
                }
            }
        },
        funcName: 'disconnectCall',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const emitErrorSignal = (error: string) => {
        signals.onError.emit({
            error: error as ExtendedInfinityErrorMessage,
            errorCode: getErrorCode(error),
        });
    };

    const getCurrentCallUuid = () => props.callUuid;

    const newCandidate = generateEndpoint({
        func: async ([params]: GetEndpointParams<'newCandidate'>, fetcher) => {
            const {
                candidate,
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            return API.newCandidate({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    callUuid,
                    breakoutUuid,
                },
                body: candidate,
                host,
            });
        },
        funcName: 'newCandidate',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const requestAudioStream = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'requestAudioStream'>,
            fetcher,
        ) => {
            const {
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                targetParticipantUuid,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            return API.requestAudioStream({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                    callUuid,
                },
                body: {
                    participant_uuid: targetParticipantUuid,
                },
                host,
            });
        },
        funcName: 'requestAudioStream',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const requestVideoStream = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'requestVideoStream'>,
            fetcher,
        ) => {
            const {
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                targetParticipantUuid,
                preferredQuality,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            return API.requestVideoStream({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                    callUuid,
                },
                body: {
                    participant_uuid: targetParticipantUuid,
                    ...(preferredQuality && {
                        preferred_quality: preferredQuality,
                    }),
                },
                host,
            });
        },
        funcName: 'requestVideoStream',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const releaseAudioStream = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'releaseAudioStream'>,
            fetcher,
        ) => {
            const {
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                targetParticipantUuid,
                mid,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            return API.releaseAudioStream({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                    callUuid,
                },
                body: {
                    participant_uuid: targetParticipantUuid,
                    mid,
                },
                host,
            });
        },
        funcName: 'releaseAudioStream',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const releaseVideoStream = generateEndpoint({
        func: async (
            [params]: GetEndpointParams<'releaseVideoStream'>,
            fetcher,
        ) => {
            const {
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                targetParticipantUuid,
                mid,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            return API.releaseVideoStream({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                    callUuid,
                },
                body: {
                    participant_uuid: targetParticipantUuid,
                    mid,
                },
                host,
            });
        },
        funcName: 'releaseVideoStream',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const present = (stream?: MediaStream) => {
        void props.call?.present(stream);
    };

    const pushStatistics = () => {
        const convertBitrate = (bitrate?: number) =>
            bitrate ? bitrate / 1000 : undefined;
        const convertJitter = (jitter?: number) =>
            jitter ? jitter * 1000 : undefined;
        const convertPercentage = (percentage?: number) =>
            percentage ? percentage * 100 : undefined;

        void statistics({
            audio: {
                rx_bitrate: convertBitrate(
                    props.latestStats?.inbound.audio?.bitrate,
                ),
                rx_codec: props.latestStats?.inbound.audio?.codec?.replace(
                    'audio/',
                    '',
                ),
                rx_historic_packet_loss: convertPercentage(
                    props.latestStats?.inbound.audio?.totalPercentageLost,
                ),
                rx_jitter: convertJitter(
                    props.latestStats?.inbound.audio?.jitter,
                ),
                rx_packets_lost: props.latestStats?.inbound.audio?.packetsLost,
                rx_packets_received:
                    props.latestStats?.inbound.audio?.packetsTransmitted,
                rx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.inbound.audio?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    props.latestStats?.outbound.audio?.bitrate,
                ),
                tx_codec: props.latestStats?.outbound.audio?.codec?.replace(
                    'audio/',
                    '',
                ),
                tx_historic_packet_loss: convertPercentage(
                    props.latestStats?.outbound.audio?.totalPercentageLost,
                ),
                tx_packets_sent:
                    props.latestStats?.outbound.audio?.packetsTransmitted,
                tx_rb_jitter: convertJitter(
                    props.latestStats?.outbound.audio?.jitter,
                ),
                tx_rb_packetslost:
                    props.latestStats?.outbound.audio?.packetsLost,
                tx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.outbound.audio?.recentPercentageLost,
                ),
            },
            video: {
                rx_bitrate: convertBitrate(
                    props.latestStats?.inbound.video?.bitrate,
                ),
                rx_codec: props.latestStats?.inbound.video?.codec?.replace(
                    'video/',
                    '',
                ),
                rx_fps: props.latestStats?.inbound.video?.framesPerSecond,
                rx_historic_packet_loss: convertPercentage(
                    props.latestStats?.inbound.video?.totalPercentageLost,
                ),
                rx_packets_lost: props.latestStats?.inbound.video?.packetsLost,
                rx_packets_received:
                    props.latestStats?.inbound.video?.packetsTransmitted,
                rx_resolution: props.latestStats?.inbound.video?.resolution,
                rx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.inbound.video?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    props.latestStats?.outbound.video?.bitrate,
                ),
                tx_codec: props.latestStats?.outbound.video?.codec?.replace(
                    'video/',
                    '',
                ),
                tx_fps: props.latestStats?.outbound.video?.framesPerSecond,
                tx_historic_packet_loss: convertPercentage(
                    props.latestStats?.outbound.video?.totalPercentageLost,
                ),
                tx_packets_sent:
                    props.latestStats?.outbound.video?.packetsTransmitted,
                tx_rb_packetslost:
                    props.latestStats?.outbound.video?.packetsLost,
                tx_resolution: props.latestStats?.outbound.video?.resolution,
                tx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.outbound.video?.recentPercentageLost,
                ),
            },
            presentation: {
                rx_bitrate: convertBitrate(
                    props.latestStats?.inbound.preso?.bitrate,
                ),
                rx_codec: props.latestStats?.inbound.preso?.codec?.replace(
                    'video/',
                    '',
                ),
                rx_fps: props.latestStats?.inbound.preso?.framesPerSecond,
                rx_historic_packet_loss: convertPercentage(
                    props.latestStats?.inbound.preso?.totalPercentageLost,
                ),
                rx_packets_lost: props.latestStats?.inbound.preso?.packetsLost,
                rx_packets_received:
                    props.latestStats?.inbound.preso?.packetsTransmitted,
                rx_resolution: props.latestStats?.inbound.preso?.resolution,
                rx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.inbound.preso?.recentPercentageLost,
                ),
                tx_bitrate: convertBitrate(
                    props.latestStats?.outbound.preso?.bitrate,
                ),
                tx_codec: props.latestStats?.outbound.preso?.codec?.replace(
                    'video/',
                    '',
                ),
                tx_fps: props.latestStats?.outbound.preso?.framesPerSecond,
                tx_historic_packet_loss: convertPercentage(
                    props.latestStats?.outbound.preso?.totalPercentageLost,
                ),
                tx_packets_sent:
                    props.latestStats?.outbound.preso?.packetsTransmitted,
                tx_rb_packetslost:
                    props.latestStats?.outbound.preso?.packetsLost,
                tx_resolution: props.latestStats?.outbound.preso?.resolution,
                tx_windowed_packet_loss: convertPercentage(
                    props.latestStats?.outbound.preso?.recentPercentageLost,
                ),
            },
        });
    };

    const releaseFloor = generateEndpoint({
        func: async ([params]: GetEndpointParams<'releaseFloor'>, fetcher) => {
            const {participantUuid, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            return API.releaseFloor({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'releaseFloor',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
        retriable: true,
    });

    const resolveBandwidth = (bandwidth?: number): Bandwidth => ({
        in: getBandwidth(bandwidth, props.bandwidthIn),
        out: getBandwidth(bandwidth, props.bandwidthOut),
    });

    const sendOffer = generateEndpoint({
        func: async ([params]: GetEndpointParams<'sendOffer'>, fetcher) => {
            const {sdp, conferenceAlias, breakoutUuid, host, participantUuid} =
                withDefaults(params);

            const body = {
                call_type: props.callMode,
                sdp,
                media_type: props.infinityCallType,
                fecc_supported: props.feccSupported,
            };

            const res = await API.callsWebrtcParticipant({
                fetcher,
                body,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                },
                host,
            });

            if (res.status === 200) {
                props.callUuid = res.data.result.call_uuid;
                signals.onAnswer.emit(res.data.result);
            } else if (res.status === 403) {
                emitErrorSignal(res.data.result);
            }

            return res;
        },
        funcName: 'sendOffer',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    const setBandwidth = (bandwidth: number) => {
        props.bandwidth = resolveBandwidth(bandwidth);
        props.call?.setBandwidth(props.bandwidth);
    };

    const setDegradationPreference = (preference: DegradationPreference) => {
        props.call?.setDegradationPreference(preference);
    };

    const setFeccSupported = (supported: boolean) => {
        props.feccSupported = supported;
    };

    const setPushStatisticsTimer = () => {
        if (getRequestClient()?.statsUpdateInterval) {
            timerIDs.pushStatistics = window.setTimeout(() => {
                pushStatistics();
                setPushStatisticsTimer();
            }, getRequestClient()?.statsUpdateInterval);
        }
    };

    /**
     * Sets whether the conference's calls are transcoded or switching. Set once
     * per conference; restarts and breakout rooms keep it.
     */
    const setCallMode = (callMode: CallMode) => {
        props.callMode = callMode;
    };

    const setStream = (stream: MediaStream) => {
        props.call?.setStream(stream);
    };

    const startCall = ({
        allow1080p,
        allowChangeOfMediaDirection,
        allowVP9,
        bandwidth: rawBandwidth,
        bandwidthIn,
        bandwidthOut,
        clientAllowH264,
        clientAllowVP8,
        clientAllowVP9,
        clientCallType = DEFAULT_CLIENT_CALLTYPE,
        dataChannelId,
        degradationPreference,
        encodingPriorities,
        infinityCallType,
        maxFrameSize,
        mediaStream,
        networkPriorities,
        numberOfReceiveAudioStreams,
        numberOfReceiveVideoStreams,
        stun,
        turn,
        useRelayCandidatesOnly,
    }: Pick<GetEndpointParams<'call'>[0], 'mediaStream'> & {
        bandwidth?: Bandwidth | number | undefined;
        maxFrameSize?: number;
        allowVP9?: boolean;
        infinityCallType?: 'video' | 'video-only' | 'audio';
        clientCallType?: ClientCallType;
        /**
         * DSCP markings for each media line
         * This has the same effect as priority, except that it only affects
         * the DSCP markings of the generated packets, as described in
         * [rfc8835] section 4.2.
         */
        networkPriorities?: MediaPriority;
        /**
         * Encoding priority for each media line
         * Indicates the priority of a RTCRtpSender, which influences the
         * bandwidth allocation among RTCRtpSender objects. It is specified in
         * [rfc8835], Section 4. The user agent is free to sub-allocate
         * bandwidth between the encodings of a RTCRtpSender.
         */
        encodingPriorities?: MediaPriority;
        degradationPreference?: DegradationPreference;
        /**
         * Allow the call to change of Transceiver direction on-demand.
         *
         * @defaultValue `true`
         */
        allowChangeOfMediaDirection?: AllowMediaChangeOfDirection;

        stun?: Stun;

        turn?: Turn;

        dataChannelId?: number;

        useRelayCandidatesOnly?: boolean;
        bandwidthIn?: number;
        bandwidthOut?: number;
        clientAllowH264?: boolean;
        clientAllowVP8?: boolean;
        clientAllowVP9?: boolean;
        allow1080p?: boolean;
        numberOfReceiveAudioStreams?: number;
        numberOfReceiveVideoStreams?: number;
    }) => {
        if (stun) {
            props.stun = stun;
        }
        if (turn) {
            props.turn = turn;
        }
        if (dataChannelId) {
            props.dataChannelId = dataChannelId;
        }
        if (useRelayCandidatesOnly !== undefined) {
            props.useRelayCandidatesOnly = useRelayCandidatesOnly;
        }
        if (bandwidthIn !== undefined) {
            props.bandwidthIn = bandwidthIn;
        }
        if (bandwidthOut !== undefined) {
            props.bandwidthOut = bandwidthOut;
        }
        if (infinityCallType) {
            props.infinityCallType = infinityCallType;
        }
        if (allow1080p !== undefined) {
            props.allow1080p = allow1080p;
        }
        if (clientAllowH264 !== undefined) {
            props.clientAllowH264 = clientAllowH264;
        }
        if (clientAllowVP8 !== undefined) {
            props.clientAllowVP8 = clientAllowVP8;
        }
        if (allowVP9 !== undefined) {
            props.allowVP9 = allowVP9;
        }
        if (clientAllowVP9 !== undefined) {
            props.clientAllowVP9 = clientAllowVP9;
        }

        // A restart or breakout keeps the receive stream counts unless the
        // caller sets them again.
        if (numberOfReceiveAudioStreams !== undefined) {
            props.numberOfReceiveAudioStreams = numberOfReceiveAudioStreams;
        }
        if (numberOfReceiveVideoStreams !== undefined) {
            props.numberOfReceiveVideoStreams = numberOfReceiveVideoStreams;
        }

        const bandwidth =
            rawBandwidth != null
                ? typeof rawBandwidth === 'object'
                    ? rawBandwidth
                    : resolveBandwidth(rawBandwidth)
                : (props.bandwidth ?? resolveBandwidth(undefined));

        props.bandwidth = bandwidth;

        props.clientCallType = matchCallType(
            clientCallType,
            toClientCallType(props.infinityCallType),
        );
        props.allowChangeOfMediaDirection = matchAllowMediaChangeOfDirection(
            allowChangeOfMediaDirection ?? props.allowChangeOfMediaDirection,
            props.infinityCallType,
        );
        logger.info(
            {
                callType: ClientCallType[props.clientCallType],
                allowChangeOfMediaDirection: props.allowChangeOfMediaDirection,
                bandwidth,
                mediaStream,
            },
            'starting call with CallType',
        );
        const callOptions = {
            sendOffer,
            ack,
            newCandidate,
            update,
            takeFloor,
            releaseFloor,
            getCurrentCallUuid,
            signals,
            eventSignals,
            callSignals,
            networkPriorities,
            encodingPriorities,
            degradationPreference,
            callMode: props.callMode,
            numberOfReceiveAudioStreams: props.numberOfReceiveAudioStreams,
            numberOfReceiveVideoStreams: props.numberOfReceiveVideoStreams,
            peerOptions: {
                allow1080p: props.allow1080p,
                allow4kPreso: clientProps.isDirectMedia,
                allowCodecSdpMunging: !clientProps.isDirectMedia,
                allowVP9: props.allowVP9 && props.clientAllowVP9,
                allowH264: props.clientAllowH264,
                allowVP8: props.clientAllowVP8,
                bandwidth,
                maxFrameSize,
                rtcConfig: {
                    bundlePolicy: 'max-bundle',
                    iceServers: [
                        ...(props.stun
                            ? [
                                  {
                                      urls: props.stun.map(stun => stun.url),
                                  },
                              ]
                            : []),
                        ...(props.turn ? props.turn : []),
                    ],
                    ...(useRelayCandidatesOnly && {
                        iceTransportPolicy: 'relay',
                    }),
                },
            },
            dataChannelId,
            mediaStream:
                typeof mediaStream === 'function' ? mediaStream() : mediaStream,
            isDirectMedia: Boolean(clientProps.isDirectMedia),
            callType: props.clientCallType,
            allowChangeOfMediaDirection: props.allowChangeOfMediaDirection,
            pcMainSignals: createPCSignals(
                [
                    'onConnectionStateChange',
                    'onIceCandidate',
                    'onIceConnectionStateChange',
                    'onReceiveIceCandidate',
                    'onRemoteStreams',
                    'onSecureCheckCode',
                    'onSignalingStateChange',
                    'onTransceiverChange',
                ],
                'call:pc:main',
            ),
            mainStatsSignals: createMainStatsSignals(),
        } satisfies CallOptions;
        props.call =
            props.callMode === 'switching'
                ? createSFUCall({
                      ...callOptions,
                      participantUuid:
                          getBreakoutParticipantUuid(
                              clientProps.breakoutUuid,
                          ) ?? clientProps.participantUuid,
                      requestAudioStream,
                      requestVideoStream,
                      releaseAudioStream,
                      releaseVideoStream,
                  })
                : createMCUCall(callOptions);
    };

    const statistics = generateEndpoint({
        func: async ([params]: GetEndpointParams<'statistics'>, fetcher) => {
            const {
                audio,
                video,
                presentation,
                conferenceAlias,
                participantUuid,
                callUuid = props.callUuid,
                host,
            } = withDefaults(params);

            if (!callUuid) {
                return;
            }

            await API.statistics({
                fetcher,
                body: {
                    audio,
                    video,
                    presentation,
                },
                params: {
                    conferenceAlias,
                    participantUuid,
                    callUuid,
                },
                host,
            });
        },
        funcName: 'statistics',
        fetcher: fetchWithDebugLog,
        requiresToken: true,
    });

    const stopPresenting = async () => {
        if (
            props.call?.presoState.send === 'connected' ||
            props.call?.presoState.send === 'connecting'
        ) {
            await props.call?.stopPresenting();
        }
    };

    const subscribeCallSignals = () => [
        callSignals.onSecureCheckCode.add(code => {
            props.secureCheckCode = code;
        }),
        callSignals.onRtcStats.add(stats => {
            props.latestStats = {
                inbound: {
                    audio:
                        stats.inbound.audio ?? props.latestStats?.inbound.audio,
                    video:
                        stats.inbound.video ?? props.latestStats?.inbound.video,
                    preso:
                        stats.inbound.preso ?? props.latestStats?.inbound.preso,
                },
                outbound: {
                    audio:
                        stats.outbound.audio ??
                        props.latestStats?.outbound.audio,
                    video:
                        stats.outbound.video ??
                        props.latestStats?.outbound.video,
                    preso:
                        stats.outbound.preso ??
                        props.latestStats?.outbound.preso,
                },
            };
        }),
        callSignals.onCallConnected.add(signals.onCallConnected.emit),
    ];

    const takeFloor = generateEndpoint({
        func: async ([params]: GetEndpointParams<'takeFloor'>, fetcher) => {
            const {participantUuid, conferenceAlias, host, breakoutUuid} =
                withDefaults(params);

            return API.takeFloor({
                fetcher,
                params: {
                    conferenceAlias,
                    participantUuid:
                        getBreakoutParticipantUuid(breakoutUuid) ??
                        participantUuid,
                    breakoutUuid,
                },
                host,
            });
        },
        funcName: 'takeFloor',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    /** Stops any active presentation, disconnects the WebRTC peer
     *  connection, and clears call/callUuid refs. Bandwidth and other
     *  call options are retained in props for reuse by subsequent calls.
     */
    const teardownCall = async () => {
        clearTimeout(timerIDs.pushStatistics);
        props.call?.disconnect();
        props.call = undefined;
        props.callUuid = undefined;
    };

    const update = generateEndpoint({
        func: async ([params]: GetEndpointParams<'update'>, fetcher) => {
            const {
                sdp,
                callUuid = props.callUuid,
                conferenceAlias,
                host,
                participantUuid,
                breakoutUuid,
                abortSignal,
            } = withDefaults(params);

            const doUpdate = async () => {
                if (!callUuid) {
                    clearTimeout(timerIDs.update);
                    return;
                }

                try {
                    const body = {
                        sdp,
                        fecc_supported: props.feccSupported,
                    };
                    const init = {signal: abortSignal};

                    const res = await API.update({
                        fetcher,
                        body,
                        params: {
                            conferenceAlias,
                            participantUuid:
                                getBreakoutParticipantUuid(breakoutUuid) ??
                                participantUuid,
                            breakoutUuid,
                            callUuid,
                        },
                        host,
                        init,
                    });

                    if (res.status === 200) {
                        clearTimeout(timerIDs.update);
                        // aggressively refresh token and reconnect event source after successful update
                        // as it could be a result of connectivity issues.
                        // This works around the fact that backoff retry could be too big at this point and cause us to expire the token
                        await getRequestClient()?.refreshToken();
                        if (
                            !present &&
                            getEventSourceManager()?.eventSource?.readyState ===
                                2
                        ) {
                            void getEventSourceManager()?.connect();
                        }

                        if (typeof res.data.result === 'string') {
                            res.data.result &&
                                signals.onAnswer.emit({
                                    sdp: res.data.result,
                                    call_uuid: callUuid,
                                });
                        } else if (res.data.result.sdp) {
                            signals.onAnswer.emit({
                                sdp: res.data.result.sdp,
                                call_uuid: res.data.result.call_uuid,
                            });
                        } else {
                            signals.onAnswer.emit(res.data.result);
                        }
                    }
                    return res;
                } catch (error) {
                    if (error instanceof Error && error.name !== 'AbortError') {
                        logger.error({error}, 'Failed /update attempt');
                        timerIDs.update = window.setTimeout(() => {
                            void doUpdate();
                        }, 1000);
                    }
                }
            };

            clearTimeout(timerIDs.update);
            return doUpdate();
        },
        funcName: 'update',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    return {
        get call() {
            return props.call;
        },
        get infinityCallType() {
            return props.infinityCallType;
        },
        get callUuid() {
            return props.callUuid;
        },
        get clientCallType() {
            return props.clientCallType;
        },
        get latestStats() {
            return props.latestStats;
        },
        get secureCheckCode() {
            return props.secureCheckCode;
        },
        cleanup,
        disconnect,
        disconnectCall,
        present,
        setBandwidth,
        setCallMode,
        setDegradationPreference,
        setFeccSupported,
        setPushStatisticsTimer,
        setStream,
        startCall,
        stopPresenting,
        subscribeCallSignals,
        teardownCall,
        pushStatistics: () => {
            // Only push statistics if the statsUpdateInterval is not set, otherwise the push will be handled by the timer
            if (!getRequestClient()?.statsUpdateInterval) {
                pushStatistics();
            }
        },
    };
};
